import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { BasicButton } from '@payer-admin-ui/components-v2/src/buttons/BasicButton';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { ExpandableContainer } from '../../ExpandableContainer/ExpandableContainer';
import { ActionCardHeader } from '../../Customer/ActionCardHeader';
import { CardSection } from '../CardSection';
import { AmountFormat } from '../../Format/AmountFormat';
import { CreditStatus } from '../../constants';
import { DateTimeFormat } from '../../Format/DateTimeFormat';
import { CreditReasons } from '../../Credit/CreditReasons';
import { RoleCheck } from '../../RoleCheck/RoleCheck';
import { ConfirmActionModal } from '../../Modal/ConfirmActionModal';
import { AmountInput } from '../../Input/AmountInput';
import { CreditStatusChip } from '../CustomerCards/CreditStatusChip';
import { Role } from '../../../../components-v2/src/types/Role';

export const CreditActionCard = ({ action, creditDecisionDetails, actions, showManualActions, headerProps }) => {
  const { t } = useTranslation();
  const credit = action?.details?.creditDecision;
  const decision = credit?.decision;
  const creditType = credit?.type;
  const rating = credit?.rating;
  const currencyCode = credit?.currencyCode;
  const creditLimitAmount = credit?.creditLimitAmount;
  const reason = action?.reason;
  const type = action?.type;
  const decisionIsControl = decision === CreditStatus.CONTROL;

  const [customLimit, setCustomLimit] = React.useState(null);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);
  const [isApproveButtonDisabled, setApproveButtonDisabled] = React.useState(false);
  const toggleApproveDialogOpen = React.useCallback(() => {
    setIsApproveDialogOpen(!isApproveDialogOpen);
  }, [isApproveDialogOpen, setIsApproveDialogOpen]);
  const toggleRejectDialogOpen = React.useCallback(() => {
    setIsRejectDialogOpen(!isRejectDialogOpen);
  }, [isRejectDialogOpen, setIsRejectDialogOpen]);
  const approve = React.useCallback(() => {
    setIsApproveDialogOpen(false);
    actions.approve(customLimit ? customLimit * 100 : null);
  }, [actions, setIsApproveDialogOpen, customLimit]);

  const reject = React.useCallback(() => {
    setIsRejectDialogOpen(false);
    actions.reject();
  }, [actions, setIsRejectDialogOpen]);

  const limitValidators = [
    Validators.notEmpty,
    Validators.notZero,
    Validators.positive,
    Validators.maxInclusive(15000),
  ];
  const hasInternationalCompanyReason = React.useMemo(
    () => credit?.reasons.find(it => it.reason === 'INTERNATIONAL_COMPANY'),
    [credit],
  );

  return (
    <ExpandableContainer
      header={
        <ActionCardHeader
          status={action.status}
          headerText={type ? t(`enums.sessionActionType.${type}`, type) : '?'}
          {...headerProps}
        />
      }
      sx={{ mb: 2 }}
    >
      <Grid container>
        <CardSection title={t('components.cards.credit.status')}>
          {decision ? <CreditStatusChip status={decision} /> : '-'}
        </CardSection>
        <CardSection title={t('components.cards.credit.limit')} size="m">
          {creditLimitAmount ? (
            <AmountFormat amount={creditLimitAmount} currencyCode={currencyCode} fractionDigits="0" />
          ) : (
            '-'
          )}
        </CardSection>
        <CardSection title={t('enums.creditRating.name')}>
          {rating ? t(`enums.creditRating.values.${rating}`, rating) : '-'}
        </CardSection>
        <CardSection title={t('components.cards.credit.decision')} size="m">
          {decision && !decisionIsControl && creditType
            ? t(`enums.creditDecisionType.values.${creditType}`, creditType)
            : '-'}
        </CardSection>
        <CardSection title={t('components.cards.credit.date')} size="m">
          {credit?.createdDate ? <DateTimeFormat dateTime={credit.createdDate} /> : '-'}
        </CardSection>
        <CardSection title={t('enums.sessionActionReason.name')} size="m">
          {reason ? t(`enums.sessionActionReason.values.${reason}`, reason) : '-'}
        </CardSection>
        <CreditReasons credit={credit} creditDecisionDetails={creditDecisionDetails} />
        {showManualActions && credit && decisionIsControl && (
          <RoleCheck requiredRole={Role.CUSTOMER_WRITE}>
            <Grid container>
              <Box display="flex" justifyContent="flex-end" width="100%">
                <BasicButton
                  color="success"
                  text={t('onboard.registration.actions.approve')}
                  sx={{ marginRight: '8px' }}
                  onClick={toggleApproveDialogOpen}
                />
                <BasicButton
                  color="error"
                  text={t('onboard.registration.actions.reject')}
                  onClick={toggleRejectDialogOpen}
                />
              </Box>
              {hasInternationalCompanyReason ? (
                <ConfirmActionModal
                  isOpen={isApproveDialogOpen}
                  message={
                    <div>
                      {t('onboard.registration.actions.creditVerification.approveDescription')}
                      <br />
                      <br />
                      <AmountInput
                        title={t('onboard.registration.actions.creditVerification.descriptionAddLimit')}
                        value={customLimit}
                        setValue={setCustomLimit}
                        unit={currencyCode}
                        extraValidators={limitValidators}
                        onValidationUpdate={setApproveButtonDisabled}
                      />
                    </div>
                  }
                  onConfirm={approve}
                  onCancel={toggleApproveDialogOpen}
                  buttonComponent={BasicButton}
                  buttonProps={{
                    color: 'success',
                    children: t('onboard.registration.actions.creditVerification.approveButton'),
                    disabled: isApproveButtonDisabled,
                  }}
                />
              ) : (
                <ConfirmActionModal
                  isOpen={isApproveDialogOpen}
                  message={t('onboard.registration.actions.creditVerification.descriptionWithLimit', {
                    amount: creditLimitAmount / 100,
                    currencyCode: currencyCode,
                  })}
                  onConfirm={approve}
                  onCancel={toggleApproveDialogOpen}
                  buttonComponent={BasicButton}
                  buttonProps={{
                    color: 'success',
                    children: t('onboard.registration.actions.creditVerification.approveButton'),
                  }}
                />
              )}
              <ConfirmActionModal
                isOpen={isRejectDialogOpen}
                message={t('onboard.registration.actions.creditVerification.rejectDescription', {
                  amount: 0,
                  currencyCode: currencyCode,
                })}
                onConfirm={reject}
                onCancel={toggleRejectDialogOpen}
                buttonComponent={BasicButton}
                buttonProps={{
                  color: 'error',
                  children: t('onboard.registration.actions.creditVerification.rejectButton'),
                }}
              />
            </Grid>
          </RoleCheck>
        )}
      </Grid>
    </ExpandableContainer>
  );
};

CreditActionCard.propTypes = {
  action: PropTypes.shape({
    status: PropTypes.string,
    type: PropTypes.string,
    reason: PropTypes.string.isRequired,
    details: PropTypes.shape({
      creditDecision: PropTypes.shape({
        decision: PropTypes.string,
        creditLimitAmount: PropTypes.number,
        currencyCode: PropTypes.string,
        createdDate: PropTypes.string,
        reasons: PropTypes.arrayOf(
          PropTypes.shape({
            reason: PropTypes.string,
          }),
        ),
        rating: PropTypes.string,
        type: PropTypes.string,
      }),
    }),
  }),
  showManualActions: PropTypes.bool.isRequired,
  creditDecisionDetails: PropTypes.object,
  actions: PropTypes.shape({
    approve: PropTypes.func.isRequired,
    reject: PropTypes.func.isRequired,
  }),
  headerProps: PropTypes.object,
};
